// Freeze verb — pick a moment in a sample and stretch that moment for
// N seconds via granular synthesis. Pitch preserved; "time" is held.

import { useCallback, useState } from 'react';
import { applyFreeze } from './freeze';
import { fromPcm, toPcm } from '../../pipeline';
import { Slider } from '../Slider';
import { SourcePicker, type PickedSource } from '../SourcePicker';
import type { VerbProps } from '../types';

export function FreezeVerb({ audioContext, onBuffer, onStatus }: VerbProps) {
  const [source, setSource] = useState<PickedSource | null>(null);
  const [position, setPosition] = useState(0.5);
  const [windowSeconds, setWindowSeconds] = useState(0.1);
  const [outputSeconds, setOutputSeconds] = useState(2);
  const [grainMs, setGrainMs] = useState(80);
  const [overlap, setOverlap] = useState(0.7);
  const [rendering, setRendering] = useState(false);

  const sourceDuration = source?.buffer.duration ?? 0;

  const handleRender = useCallback(async () => {
    if (!source || !audioContext) return;
    setRendering(true);
    try {
      const result = applyFreeze(toPcm(source.buffer), {
        positionSeconds: position * sourceDuration,
        windowSeconds,
        outputSeconds,
        grainMs,
        overlap,
      });
      const buffer = fromPcm(audioContext, result);
      const sourceIds = source.sampleId ? [source.sampleId] : [];
      onBuffer(buffer, {
        suggestedName: `${source.name}_freeze`,
        origin: {
          kind: 'freeze',
          sourceSampleId: source.sampleId,
          positionSeconds: position * sourceDuration,
          windowSeconds,
          outputSeconds,
          grainMs,
          overlap,
        },
        sourceSampleIds: sourceIds.length > 0 ? sourceIds : undefined,
      });
      onStatus('Rendered — trim and save.', 'ok');
    } catch (e) {
      onStatus(
        'Render failed: ' + (e instanceof Error ? e.message : String(e)),
        'err',
      );
    } finally {
      setRendering(false);
    }
  }, [
    source,
    audioContext,
    position,
    sourceDuration,
    windowSeconds,
    outputSeconds,
    grainMs,
    overlap,
    onBuffer,
    onStatus,
  ]);

  return (
    <div className="flex flex-col gap-3 min-h-[200px]">
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-400">
        Hold a moment in the source and stretch it indefinitely. Drop the
        position onto a sustain or held chord for the cleanest texture.
      </div>
      <SourcePicker
        audioContext={audioContext}
        selectedId={source?.sampleId}
        onPicked={setSource}
        onStatus={onStatus}
        label="Source"
      />
      <Slider
        label="Position"
        value={position}
        min={0}
        max={1}
        step={0.001}
        display={
          source
            ? `${(position * sourceDuration).toFixed(2)}s / ${sourceDuration.toFixed(2)}s`
            : '—'
        }
        onChange={setPosition}
      />
      <Slider
        label="Window"
        value={windowSeconds}
        min={0.01}
        max={1}
        step={0.01}
        display={`${windowSeconds.toFixed(2)} s`}
        onChange={setWindowSeconds}
      />
      <Slider
        label="Length"
        value={outputSeconds}
        min={0.5}
        max={10}
        step={0.5}
        display={`${outputSeconds.toFixed(1)} s`}
        onChange={setOutputSeconds}
      />
      <Slider
        label="Grain"
        value={grainMs}
        min={20}
        max={300}
        step={5}
        display={`${grainMs} ms`}
        onChange={setGrainMs}
      />
      <Slider
        label="Overlap"
        value={overlap}
        min={0.1}
        max={0.95}
        step={0.05}
        display={overlap.toFixed(2)}
        onChange={setOverlap}
      />
      <button
        type="button"
        onClick={() => void handleRender()}
        disabled={!source || rendering}
        className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
      >
        {rendering ? 'Rendering…' : '● Render'}
      </button>
    </div>
  );
}

